import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw, Plus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { clearanceAPI } from '../../lib/clearanceAPI';
import { errText } from '../../lib/lettersAPI';
import { ClearanceCaseCard } from './ClearanceCaseCard';
import { ContactDialog } from './ClearanceDialogs';

const GateBox = ({ clientId, gate, meta, reload }) => {
  const [reason, setReason] = useState('');
  if (!gate?.required) return <p className="text-xs text-muted-foreground" data-testid="pc-gate-optional">Professional clearance is optional for onboarding (practice setting).</p>;
  const go = async () => { try { await clearanceAPI.gateOverride(clientId, reason); toast.success('Override recorded'); setReason(''); reload(); } catch (e) { toast.error(errText(e)); } };
  return (
    <div className="rounded-md border p-3 text-sm space-y-2" data-testid="pc-gate">
      {gate.override ? <p className="text-amber-800" data-testid="pc-gate-overridden">Onboarding gate overridden by {gate.override.by_name} on {new Date(gate.override.at).toLocaleDateString('en-AU')}: “{gate.override.reason}”</p>
        : gate.hold ? <p className="text-red-700" data-testid="pc-gate-hold">Onboarding completion is on hold: {gate.hold}.</p>
          : <p className="text-emerald-700" data-testid="pc-gate-clear">Clearance requirement for onboarding is satisfied.</p>}
      {gate.hold && meta?.me?.is_manager && !gate.override && <div className="flex gap-2">
        <Textarea rows={1} placeholder="Reason for overriding the clearance requirement" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pc-gate-override-reason" />
        <Button variant="outline" disabled={reason.trim().length < 10} onClick={go} data-testid="pc-gate-override-btn">Override</Button></div>}
    </div>
  );
};

export const ClearancePanel = ({ clientId }) => {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => clearanceAPI.client(clientId).then(setData).catch((e) => toast.error(errText(e))), [clientId]);
  useEffect(() => { load(); clearanceAPI.meta().then(setMeta).catch(() => {}); }, [load]);
  const sync = async () => { try { await clearanceAPI.syncOnboarding(clientId); toast.success('Synced from onboarding'); load(); } catch (e) { toast.error(errText(e)); } };
  const create = async (contact) => { await clearanceAPI.create({ client_id: clientId, contact }); toast.success('Clearance case created'); load(); };
  const ans = data?.onboarding?.has_previous_accountant;
  return (
    <Card data-testid="clearance-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Professional Clearance</CardTitle>
        {meta?.me?.can_edit && <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={sync} data-testid="pc-sync-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" />Sync from onboarding</Button>
          <Button size="sm" onClick={() => setAdding(true)} data-testid="pc-new-case-btn"><Plus className="w-3.5 h-3.5 mr-1" />New case</Button></div>}
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">A response from the previous accountant is not a legal approval; acceptance is your professional judgement under APES 110 and TPB requirements.</p>
        <div className="text-xs" data-testid="pc-onboarding-answer">Onboarding answer: <Badge variant="outline">{ans === true || ans === 'yes' ? 'Yes – has a previous accountant' : ans === false || ans === 'no' ? 'No' : ans === 'unknown' ? 'Unknown – client to confirm' : 'Not answered'}</Badge></div>
        <GateBox clientId={clientId} gate={data?.gate} meta={meta} reload={load} />
        {data?.cases?.length === 0 && <p className="text-sm text-muted-foreground" data-testid="pc-no-cases">No clearance case yet.</p>}
        {(data?.cases || []).map((c) => <ClearanceCaseCard key={c.id} c={c} meta={meta} reload={load} />)}
      </CardContent>
      <ContactDialog open={adding} onClose={() => setAdding(false)} onSave={create} title="New professional clearance case" />
    </Card>
  );
};
